import React, { useEffect, useMemo } from 'react';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space } from 'antd';
import {
  TeamOutlined,
  FileDoneOutlined,
  DashboardOutlined,
  UserOutlined,
  LogoutOutlined,
  ProfileOutlined,
  UserAddOutlined,
  ScheduleOutlined,
  IdcardOutlined,
  BarChartOutlined,
  FileTextOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './Layout.module.css';
import { logout, setOrgId } from '../../redux/authSlice';
import { setOrganisationsFromAuth } from '../../redux/organisationDepartmentSlice';
import { setShowAllRequests } from '../../redux/vacancyRequestsSlice';
import { setShowAllVacancies } from '../../redux/vacanciesSlice';

const { Header, Sider, Content } = AntLayout;

const menuItems = [
  {
    key: '/vacancy-requests',
    icon: <FileDoneOutlined />,
    label: 'Заявки',
    path: '/vacancy-requests'
  },
  {
    key: '/vacancies',
    icon: <IdcardOutlined />,
    label: 'Вакансии',
    path: '/vacancies'
  },
  {
    key: '/candidates',
    icon: <TeamOutlined />,
    label: 'Кандидаты',
    path: '/candidates'
  },
  {
    key: '/tasks',
    icon: <ScheduleOutlined />,
    label: 'Задачи',
    path: '/tasks'
  },
  {
    key: 'analytics',
    icon: <BarChartOutlined />,
    label: 'Аналитика и отчетность',
    children: [
      {
        key: '/reports/vacancies',
        icon: <FileTextOutlined />,
        label: 'Отчетность по вакансиям',
        path: '/reports/vacancies'
      },
      {
        key: '/reports/recruiters',
        icon: <UsergroupAddOutlined />,
        label: 'Отчетность по рекрутерам',
        path: '/reports/recruiters'
      }
    ]
  }
];

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { orgId, role, userOrganisations } = useSelector((state) => state.auth);
  const storedRole = typeof window !== 'undefined' ? localStorage.getItem('user_role') : null;
  const effectiveRole = role || storedRole;
  const { organisations } = useSelector((state) => state.organisationDepartment);

  // console.log('role', role);

  const filteredMenuItems = useMemo(() => {
    if (effectiveRole === 'employee') {
      return menuItems.filter(item => item.key === '/tasks');
    }
    // Показываем аналитику только для superadmin, admin, hr
    // leader и user - нет доступа
    const allowedRolesForAnalytics = ['superadmin', 'admin', 'hr'];
    if (!allowedRolesForAnalytics.includes(effectiveRole)) {
      return menuItems.filter(item => item.key !== 'analytics');
    }
    return menuItems;
  }, [effectiveRole]);

  useEffect(() => {
    if (userOrganisations && userOrganisations.length > 0 && organisations.length === 0) {
      dispatch(setOrganisationsFromAuth(userOrganisations));
    }
  }, [dispatch, userOrganisations, organisations.length]);

  useEffect(() => {
    if (effectiveRole === 'employee') {
      const allowedPaths = ['/tasks', '/profile', '/dashboard'];
      const currentPath = location.pathname;

      if (!allowedPaths.some(path => currentPath.startsWith(path))) {
        navigate('/tasks', { replace: true });
      }
    }
  }, [effectiveRole, location.pathname, navigate]);

  // const activeTab = menuItems.find(item => location.pathname.startsWith(item.path))
  // const activeTabName = activeTab?.label || 'Панель управления';

  const userMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Дашборд',
      onClick: () => navigate('/dashboard')
    },
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile')
    },
    {
      type: 'divider',
    },
    {
      key: 'signup',
      icon: <UserAddOutlined />,
      label: 'Регистрация пользователя',
      onClick: () => {
        navigate('/register');
      }
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      danger: true,
      onClick: () => {
        dispatch(logout());
        navigate('/login');
      }
    }
  ];

  const selectedOrgValue = useMemo(() => {
    return orgId ?? (localStorage.getItem('organisation_id') ? Number(localStorage.getItem('organisation_id')) : undefined);
  }, [orgId]);

  const uniqueOrganisations = useMemo(() => {
    if (!organisations || organisations.length === 0) return [];

    const seen = new Map();
    organisations.forEach((org) => {
      const orgId = org.organisation_id ?? org.id;
      if (orgId && !seen.has(orgId)) {
        seen.set(orgId, org);
      }
    });

    return Array.from(seen.values());
  }, [organisations]);

  const manualPdfPath = `${process.env.PUBLIC_URL || ''}/Инструкция_по_работе_с_сервисом_HR_платформа.pdf`;
  const manualPdfUrl = encodeURI(manualPdfPath);

  return (
    <AntLayout style={{ minHeight: '100vh', maxHeight: '100vh', overflowY: 'auto' }}>

      <Sider width={250} theme="dark">
        <div className="logo" style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold',
          background: '#001529'
        }}>
          HR-платформа
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={filteredMenuItems.map(item => {
            if (item.children) {
              return {
                key: item.key,
                icon: item.icon,
                label: item.label,
                children: item.children.map(child => ({
                  key: child.key,
                  icon: child.icon,
                  label: <Link to={child.path}>{child.label}</Link>,
                }))
              };
            }
            return {
              key: item.key,
              icon: item.icon,
              label: <Link to={item.path}>{item.label}</Link>,
            };
          })}
        />
        <div style={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          padding: '16px',
          backgroundColor: '#001529',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <select
            style={{
              flex: 1,
              backgroundColor: '#001529',
              color: 'rgba(255, 255, 255, 0.65)',
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              padding: '4px 8px',
              borderRadius: '4px',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='rgba(255, 255, 255, 0.65)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
            value={selectedOrgValue ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                dispatch(setOrgId(null));
                localStorage.removeItem('organisation_id');
                // Сбрасываем состояния кнопок "Показать все"
                dispatch(setShowAllRequests(false));
                dispatch(setShowAllVacancies(false));
                return;
              }
              const selectedOrg = Number(value);
              localStorage.setItem('organisation_id', selectedOrg);
              dispatch(setOrgId(selectedOrg));
              dispatch(setShowAllRequests(false));
              dispatch(setShowAllVacancies(false));
            }}
          >
            {uniqueOrganisations && uniqueOrganisations.length > 0 ? (
              <>
                {uniqueOrganisations.map((opt) => (
                  <option key={opt.id} value={opt.organisation_id ?? opt.id}>
                    {opt.name}
                  </option>
                ))}
              </>
            ) : (
              <option value="">Организации не найдены</option>
            )}
          </select>
        </div>
      </Sider>

      <AntLayout>
        <Header style={{
          padding: '0 24px',
          background: '#001529',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'white'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '600' }}>

          </div>

          <Space size="large">
            <a
              href={manualPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'white' }}
              onClick={(e) => { e.preventDefault(); window.open(manualPdfUrl, '_blank', 'noopener,noreferrer'); }}
            >
              Инструкция по работе с сервисом «HR-платформа»
            </a>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                <Avatar
                  style={{
                    backgroundColor: '#1890ff',
                    verticalAlign: 'middle'
                  }}
                  icon={<UserOutlined />}
                />
                <span>{localStorage.getItem('first_name')} {localStorage.getItem('last_name')}</span>
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{
          // padding: 24,
          minHeight: 280,
          background: '#fff',
          borderRadius: '8px',
          overflowY: 'auto'
        }}>
          < Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;